import { Booking, Location, Service } from '../types';
import { toLocalDateString } from './dateUtils';
export { toLocalDateString } from './dateUtils';

// HH:MM → Minuten seit Mitternacht
const timeToMinutes = (time: string): number => {
  if (!time || !time.includes(':')) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Minuten seit Mitternacht → HH:MM
const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
  const mins = (minutes % 60).toString().padStart(2, '0');
  return `${hours}:${mins}`;
};

// Öffnungszeiten für einen Tag ermitteln (individuelle > wöchentliche > allgemeine)
const getOpeningHours = (date: Date, location: Location): { start: string; end: string } | null => {
  const weekdayNumber = date.getDay() === 0 ? 7 : date.getDay(); // Mo=1, So=7
  const individualTimes = location.individualOpenTimes?.[weekdayNumber];

  if (individualTimes) {
    if (individualTimes.closed) return null;
    return { start: individualTimes.start, end: individualTimes.end };
  }

  const dayOfWeekName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const weeklyHours = (location.openingHours as any)?.[dayOfWeekName];
  if (weeklyHours) {
    if (weeklyHours.closed) return null;
    return { start: weeklyHours.start, end: weeklyHours.end };
  }

  if (location.openDays && !location.openDays.includes(weekdayNumber)) return null;
  if (!location.openTimes) return null;
  return location.openTimes;
};

// Alle belegten Intervalle für einen Tag berechnen (Buchungen + Puffer + Mittagspause)
const getOccupiedIntervals = (
  date: Date,
  location: Location,
  bookings: Booking[],
  services: Service[]
): Array<{ start: number; end: number }> => {
  const dateISO = toLocalDateString(date);
  const bufferTime = location.bufferTime ?? location.buffer_time ?? 0;

  const bookingsForDay = bookings.filter(
    b =>
      b.bookingDate === dateISO &&
      b.locationId === location.id &&
      b.status !== 'cancelled'
  );

  const intervals: Array<{ start: number; end: number }> = bookingsForDay.map(booking => {
    const bookedService = services.find(s => s.id === booking.serviceId);
    const duration = bookedService?.duration ?? 60;
    const startMin = timeToMinutes(booking.bookingTime);
    return {
      start: Math.max(0, startMin - bufferTime),
      end: startMin + duration + bufferTime,
    };
  });

  // Mittagspause als blockiertes Intervall hinzufügen
  const lunchStart = location.lunchWindowStart ?? location.lunch_break_start ?? (location as any).lunch_window_start;
  const lunchEnd = location.lunchWindowEnd ?? location.lunch_break_end ?? (location as any).lunch_window_end;
  const lunchDuration = location.lunchBreakDuration ?? location.lunch_break_duration;

  if (lunchStart && lunchEnd) {
    intervals.push({
      start: timeToMinutes(lunchStart),
      end: timeToMinutes(lunchEnd),
    });
  } else if (lunchStart && lunchDuration) {
    const s = timeToMinutes(lunchStart);
    intervals.push({ start: s, end: s + lunchDuration });
  }

  // Sortieren und überlappende Intervalle zusammenführen (Merge)
  intervals.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const iv of intervals) {
    if (merged.length === 0 || iv.start > merged[merged.length - 1].end) {
      merged.push({ ...iv });
    } else {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, iv.end);
    }
  }
  return merged;
};

// Freie Zeitfenster zwischen belegten Intervallen berechnen
const getFreeWindows = (
  openTime: number,
  closeTime: number,
  occupied: Array<{ start: number; end: number }>
): Array<{ start: number; end: number }> => {
  const windows: Array<{ start: number; end: number }> = [];
  let cursor = openTime;

  for (const iv of occupied) {
    if (iv.start > cursor) {
      windows.push({ start: cursor, end: iv.start });
    }
    if (iv.end > cursor) cursor = iv.end;
  }

  if (cursor < closeTime) {
    windows.push({ start: cursor, end: closeTime });
  }

  return windows;
};

// Alle verfügbaren Slots für ein Datum, einen Service und einen Standort.
// Strategie: Schritt = Servicedauer + Puffer — keine fixen Intervalle (15/30min).
// Durch jedes freie Fenster wird in serviceDuration-Schritten gegangen, sodass
// sich Buchungen lückenlos aneinanderreihen und keine künstlichen Slots entstehen.
export const generateTimeSlots = (
  date: Date,
  service: Service,
  location: Location,
  bookings: Booking[],
  services: Service[],
  overrideDuration?: number
): string[] => {
  if (!location || !service || !date) return [];

  const openingHours = getOpeningHours(date, location);
  if (!openingHours) return [];

  const openTime = timeToMinutes(openingHours.start);
  const closeTime = timeToMinutes(openingHours.end);
  const serviceDuration = overrideDuration ?? service.duration;
  const bufferTime = location.bufferTime ?? location.buffer_time ?? 0;
  // Schritt = Behandlungsdauer + Puffer (kein fixer 15/30min-Raster)
  const step = serviceDuration + bufferTime;

  const occupied = getOccupiedIntervals(date, location, bookings, services);
  const freeWindows = getFreeWindows(openTime, closeTime, occupied);

  const slots: string[] = [];
  for (const window of freeWindows) {
    // Durch jedes freie Fenster in Behandlungsdauer-Schritten laufen
    for (let t = window.start; t + serviceDuration <= window.end; t += step) {
      slots.push(minutesToTime(t));
    }
  }

  return slots;
};

// Empfohlene Slots: bevorzugt "tight packing" (direkt nach bestehenden Buchungen)
export const generateRecommendedTimeSlots = (
  date: Date,
  service: Service,
  location: Location,
  bookings: Booking[],
  services: Service[],
  maxSlots = 6
): string[] => {
  if (!location || !service || !date) return [];

  const openingHours = getOpeningHours(date, location);
  if (!openingHours) return [];

  const openTime = timeToMinutes(openingHours.start);
  const closeTime = timeToMinutes(openingHours.end);
  const serviceDuration = service.duration;
  const bufferTime = location.bufferTime ?? location.buffer_time ?? 0;
  const stepSize = location.slotDuration ?? location.slot_duration ?? 15;

  const occupied = getOccupiedIntervals(date, location, bookings, services);
  const freeWindows = getFreeWindows(openTime, closeTime, occupied);

  // Alle gültigen Slots berechnen (als Minutenzahl)
  const allSlots: number[] = [];
  for (const window of freeWindows) {
    for (let t = window.start; t + serviceDuration + bufferTime <= window.end; t += stepSize) {
      allSlots.push(t);
    }
  }

  if (allSlots.length === 0) return [];

  // "Tight packing" Slots: direkt am Öffnungsbeginn oder direkt nach einer Buchung
  const tightSlots = new Set<number>();

  // Tagesbeginn
  if (allSlots[0] === openTime) tightSlots.add(openTime);

  // Direkt nach jeder belegten Buchung (iv.end = Buchungsende inkl. Buffer)
  for (const iv of occupied) {
    if (allSlots.includes(iv.end)) tightSlots.add(iv.end);
  }

  // Tight-Slots zuerst, dann auffüllen bis maxSlots
  const result = new Set<number>(tightSlots);
  for (const s of allSlots) {
    if (result.size >= maxSlots) break;
    result.add(s);
  }

  return Array.from(result)
    .sort((a, b) => a - b)
    .slice(0, maxSlots)
    .map(minutesToTime);
};

// Prüft ob ein bestimmter Zeitslot buchbar ist
export const isBookingTimeAvailable = (
  time: string,
  date: Date,
  service: Service,
  location: Location,
  bookings: Booking[],
  services: Service[]
): boolean => {
  if (!time || !date || !service || !location) return false;

  const openingHours = getOpeningHours(date, location);
  if (!openingHours) return false;

  const openTime = timeToMinutes(openingHours.start);
  const closeTime = timeToMinutes(openingHours.end);
  const slotStart = timeToMinutes(time);
  const bufferTime = location.bufferTime ?? location.buffer_time ?? 0;
  const slotEnd = slotStart + service.duration + bufferTime;

  // Außerhalb der Öffnungszeiten?
  if (slotStart < openTime || slotEnd > closeTime) return false;

  // Überschneidung mit belegtem Intervall?
  const occupied = getOccupiedIntervals(date, location, bookings, services);
  for (const iv of occupied) {
    if (slotStart < iv.end && slotEnd > iv.start) return false;
  }

  return true;
};
